import { supabase } from '@/integrations/supabase/client';
import { EventAccess, EventAccessRole, EventAccessStatus } from '@/types';

// The generated `Database` type doesn't yet know about `event_access`. Until
// `supabase gen types typescript` is re-run, this service casts through
// `unknown`. Mirrors the pattern in `mesaService.ts` and `useSubscription.tsx`.

interface AccessRow {
  id: string;
  evento_id: string;
  invited_email: string;
  invited_user_id: string | null;
  role: EventAccessRole;
  status: EventAccessStatus;
  invited_by: string;
  created_at: string;
  accepted_at: string | null;
  updated_at: string;
}

interface UntypedError {
  code?: string;
  message?: string;
}

interface UntypedClient {
  from: (t: string) => {
    select: (cols?: string) => {
      eq: (col: string, value: unknown) => {
        order: (col: string, opts?: { ascending?: boolean }) => Promise<{
          data: AccessRow[] | null;
          error: UntypedError | null;
        }>;
      };
    };
    update: (patch: Partial<AccessRow>) => {
      eq: (col: string, value: unknown) => {
        select: () => {
          single: () => Promise<{ data: AccessRow | null; error: UntypedError | null }>;
        };
      };
    };
    delete: () => {
      eq: (col: string, value: unknown) => Promise<{ error: UntypedError | null }>;
    };
  };
}

const client = supabase as unknown as UntypedClient;

const rowToAccess = (row: AccessRow): EventAccess => ({
  id: row.id,
  evento_id: row.evento_id,
  invited_email: row.invited_email,
  invited_user_id: row.invited_user_id,
  role: row.role,
  status: row.status,
  invited_by: row.invited_by,
  created_at: new Date(row.created_at),
  accepted_at: row.accepted_at ? new Date(row.accepted_at) : null,
});

export interface InviteEventUserResponse {
  ok: true;
  access_id: string;
  invited: boolean;
}

export const eventAccessService = {
  listForEvent: async (eventId: string): Promise<EventAccess[]> => {
    const { data, error } = await client
      .from('event_access')
      .select('*')
      .eq('evento_id', eventId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message ?? 'Error cargando accesos');
    return (data ?? []).map(rowToAccess);
  },

  updateRole: async (id: string, role: EventAccessRole): Promise<EventAccess> => {
    const { data, error } = await client
      .from('event_access')
      .update({ role })
      .eq('id', id)
      .select()
      .single();
    if (error || !data) throw new Error(error?.message ?? 'No se pudo actualizar el rol');
    return rowToAccess(data);
  },

  revoke: async (id: string): Promise<void> => {
    const { error } = await client.from('event_access').delete().eq('id', id);
    if (error) throw new Error(error.message ?? 'No se pudo revocar el acceso');
  },

  /**
   * Calls the `inviteEventUser` Edge Function which (1) verifies the caller
   * owns the event, (2) upserts the `event_access` row, (3) sends the
   * Supabase auth invite email via the admin SDK.
   */
  invite: async (params: {
    eventoId: string;
    email: string;
    role: EventAccessRole;
  }): Promise<InviteEventUserResponse> => {
    const { data: { session }, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr || !session?.access_token) {
      throw new Error('Usuario no autenticado');
    }
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/inviteEventUser`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          evento_id: params.eventoId,
          email: params.email,
          role: params.role,
        }),
      },
    );
    if (!response.ok) {
      let message = 'No se pudo enviar la invitación';
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch { /* ignore */ }
      throw new Error(message);
    }
    return (await response.json()) as InviteEventUserResponse;
  },
};
